import { localDateKey } from '../../../../utils/datetime';
import React from 'react';
import Modal from '../../../../components/Modal';
import { Button, Select } from '../../../../components';
import { InitialsAvatar } from '../shared';
import { useDutyRoster } from '../DutyRosterContext';

const CoverModal = () => {
    const { weekDates, coverModal, coverForm, setCoverForm, coverStaff, coverStaffLoading, savingCover, onCoverEmployeeChange, closeCover, submitCover } = useDutyRoster();
    return (
            <Modal
                isOpen={!!coverModal}
                onClose={closeCover}
                title="Add temporary cover"
                maxWidth="max-w-[560px]"
            >
                {coverModal && (() => {
                    const isPick = coverModal.mode === 'pick';
                    const fmtDayLabel = (ds) => {
                        const dt = new Date(`${ds}T00:00:00`);
                        const wd = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dt.getDay()];
                        const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()];
                        return `${wd} ${dt.getDate()} ${mo}`;
                    };
                    return (
                        <div className="space-y-4 pt-1">
                            <p className="text-[12px] text-[#64748B] dark:text-gray-400 -mt-1">Assign a carer to work a day they're normally off or on holiday.</p>

                            {isPick && coverModal.group && (
                                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#F2F6FC] dark:bg-[#31373f] text-[#3b4a61] dark:text-gray-200 text-[12px] font-bold">
                                    <span className="w-2.5 h-2.5 rounded-sm bg-[#2563EB]" />
                                    {coverModal.group.name}
                                </div>
                            )}

                            {isPick ? (() => {
                                // Scope the carer list to this team's own staff.
                                const pickStaff = coverModal.group ? coverStaff.filter((s) => s.group_id === coverModal.group.id) : coverStaff;
                                return (
                                /* Pick mode — choose the carer + date directly. */
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Staff member</label>
                                        <Select
                                            className="w-full"
                                            value={coverForm.employee_id}
                                            onChange={onCoverEmployeeChange}
                                            disabled={coverStaffLoading}
                                            placeholder={coverStaffLoading ? 'Loading…' : (pickStaff.length === 0 ? 'No staff in this team' : 'Select…')}
                                            options={pickStaff.map((s) => ({ value: String(s.id), label: `${s.name} · ${s.group_name || 'Unassigned'}` }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Date</label>
                                        <Select
                                            className="w-full"
                                            value={coverForm.date}
                                            onChange={(v) => setCoverForm((f) => ({ ...f, date: v }))}
                                            options={weekDates.map((wdt) => { const ds = localDateKey(wdt); return { value: ds, label: fmtDayLabel(ds) }; })}
                                        />
                                    </div>
                                </div>
                                );
                            })() : (
                                /* Card mode — carer + date are fixed (came from the rest card). */
                                <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F2F6FC] dark:bg-[#31373f]">
                                    <InitialsAvatar name={coverModal.emp.name} size="md" />
                                    <div className="min-w-0">
                                        <div className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200 truncate">{coverModal.emp.name}</div>
                                        <div className="text-[11px] text-[#64748B] dark:text-gray-400 font-semibold">{fmtDayLabel(coverForm.date)}</div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Start time</label>
                                    <input
                                        type="time"
                                        value={coverForm.start_time}
                                        onChange={(e) => setCoverForm((f) => ({ ...f, start_time: e.target.value }))}
                                        className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">End time</label>
                                    <input
                                        type="time"
                                        value={coverForm.end_time}
                                        onChange={(e) => setCoverForm((f) => ({ ...f, end_time: e.target.value }))}
                                        className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5">Note (optional)</label>
                                <input
                                    type="text"
                                    value={coverForm.note}
                                    onChange={(e) => setCoverForm((f) => ({ ...f, note: e.target.value }))}
                                    placeholder="e.g. covering sickness"
                                    className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 dark:placeholder:text-gray-500 focus:border-blue-500 outline-none text-[13px]"
                                />
                            </div>

                            <p className="text-[11px] text-[#64748B] dark:text-gray-400 bg-[#F8FAFC] dark:bg-[#20242c] border border-[#E2E8F0] dark:border-gray-800 rounded-lg px-3 py-2">
                                This schedules a one-off shift for this carer on this date only. Their weekly roster is not changed.
                            </p>

                            <div className="flex items-center justify-end gap-2.5 pt-1">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={closeCover}
                                    disabled={savingCover}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={submitCover}
                                    loading={savingCover}
                                >
                                    Assign cover
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>
    );
};

export default CoverModal;
